import { useCallback, useEffect, useRef, useState } from 'react';

export interface ScrollEdges {
  /**
   * A **callback ref** for the scrolling element.
   *
   * Callback rather than `{ ref }` for the same reason as `useIntersection`:
   * React Compiler's `refs` rule is at error level and forbids reading
   * `ref.current` during render, and a callback ref is also strictly more
   * correct — it runs the moment the node mounts, so a body that appears after
   * the first paint (a dialog opening, a lazily loaded form) is measured
   * immediately rather than one effect pass late.
   */
  ref: (node: HTMLElement | null) => void;
  /** Nothing is hidden above the visible area. */
  isAtStart: boolean;
  /** Nothing is hidden below it. */
  isAtEnd: boolean;
}

/**
 * The resting state: both edges reached, i.e. no separators.
 *
 * A scroller must never draw an edge line before it has been measured — a
 * dialog that flashes a rule under its header on open looks broken, and the
 * measurement is one synchronous read away.
 */
const AT_REST = { isAtStart: true, isAtEnd: true } as const;

/**
 * Sub-pixel tolerance.
 *
 * `scrollTop` is fractional under a browser zoom and on a trackpad fling, so
 * `scrollTop === scrollHeight - clientHeight` is a comparison that can be false
 * forever at the bottom of a list. One pixel of slack is invisible and makes the
 * end edge reachable at every zoom level.
 */
const EDGE_TOLERANCE_PX = 1;

/**
 * Tracks whether a scrolling element is at its start and/or end edge.
 *
 * Exists so an overlay body can show a separator **only** while there is content
 * past that edge (see `SCROLL_EDGE_CLASSES`). Without it a scrolled body and a
 * clipped one look identical, which is precisely the confusion P10.1 is here to
 * remove — the operator cannot tell whether the dialog ends or merely stops.
 *
 * Vertical only, deliberately: the horizontal equivalent has to reason about the
 * sign of `scrollLeft`, which differs between engines under RTL, and no scroller
 * in the portal needs inline-axis edges. Adding it later means adding the sign
 * handling once, here, rather than at a call site (U7).
 *
 * Three sources, none redundant: a `scroll` listener for the position, a
 * `ResizeObserver` over the scroller **and its children** for content that grows
 * without the DOM changing, and a `MutationObserver` for content that arrives.
 * The children matter as much as the scroller: at the clamp — i.e. whenever
 * scrolling is actually happening — new content changes `scrollHeight` and
 * leaves the scroller's own box untouched, so watching it alone would miss
 * exactly the case this hook exists for.
 */
export function useScrollEdges(): ScrollEdges {
  const [edges, setEdges] = useState<{ isAtStart: boolean; isAtEnd: boolean }>(AT_REST);
  const cleanupRef = useRef<(() => void) | null>(null);
  // The last pair actually published. React's own bail-out (returning the
  // previous state object from the updater) is not enough here: it still renders
  // the component ONE more time before it takes effect, which on a momentum
  // fling means a re-render per frame for a value that did not change. The ref
  // is what makes an unchanged measurement cost nothing at all.
  const publishedRef = useRef<{ isAtStart: boolean; isAtEnd: boolean }>(AT_REST);

  // Detach on unmount. The callback ref handles a node SWAP; this covers the
  // component going away while a node is still attached, which no ref call
  // announces.
  useEffect(
    () => () => {
      cleanupRef.current?.();
      cleanupRef.current = null;
    },
    [],
  );

  const ref = useCallback((node: HTMLElement | null) => {
    cleanupRef.current?.();
    cleanupRef.current = null;

    const publish = (next: { isAtStart: boolean; isAtEnd: boolean }): void => {
      const previous = publishedRef.current;

      if (previous.isAtStart === next.isAtStart && previous.isAtEnd === next.isAtEnd) {
        return;
      }

      publishedRef.current = next;
      setEdges(next);
    };

    if (node === null) {
      // Back to rest: whatever renders next must not inherit the separators of a
      // scroller that is no longer on screen.
      publish(AT_REST);
      return;
    }

    const measure = (): void => {
      const distance = node.scrollHeight - node.clientHeight;

      // A scroll event fires per wheel tick and per frame of a fling; the pair
      // it produces changes twice per scroll. Everything else is discarded by
      // `publish` before it can reach React.
      publish(
        distance <= EDGE_TOLERANCE_PX
          ? AT_REST
          : {
              isAtStart: node.scrollTop <= EDGE_TOLERANCE_PX,
              isAtEnd: node.scrollTop >= distance - EDGE_TOLERANCE_PX,
            },
      );
    };

    measure();

    node.addEventListener('scroll', measure, { passive: true });

    /**
     * The scroller AND its content, because observing only the scroller misses
     * the case this exists for.
     *
     * Once the body is at its clamp — which is whenever scrolling is actually
     * happening — content growing changes `scrollHeight` and leaves the body's
     * own border box exactly where it was, so a `ResizeObserver` watching the
     * body alone never fires. A validation message appears, there is now content
     * below, and the bottom rule stays hidden. Watching the children covers the
     * growth that is a resize (an image finishing, a row expanding); the
     * `MutationObserver` below covers the growth that is a DOM change, and
     * re-points the resize observer at whatever the children are now.
     */
    const resizeObserver = new ResizeObserver(measure);
    const observeContent = (): void => {
      resizeObserver.disconnect();
      resizeObserver.observe(node);
      for (const child of node.children) {
        resizeObserver.observe(child);
      }
    };

    observeContent();

    const mutationObserver = new MutationObserver(() => {
      observeContent();
      measure();
    });
    mutationObserver.observe(node, { childList: true, subtree: true, characterData: true });

    cleanupRef.current = () => {
      node.removeEventListener('scroll', measure);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, []);

  return { ref, isAtStart: edges.isAtStart, isAtEnd: edges.isAtEnd };
}
